/**
Copyright (c) 2022–2026 Podstack.ai
All rights reserved.
Proprietary and Confidential.
Unauthorized copying of this file, via any medium, is strictly prohibited.
This file is part of the Podstack Platform.
*/

import React, { useState, useMemo, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { PlusIcon } from '@heroicons/react/24/outline';
import Header from '../components/layout/Header';
import ProjectSelector from '../components/storage/ProjectSelector';
import BucketFilters from '../components/storage/BucketFilters';
import BucketListTable from '../components/storage/BucketListTable';
import BucketDetails from '../components/storage/BucketDetails';
import CreateBucketModal from '../components/storage/CreateBucketModal';
import DeleteBucketModal from '../components/storage/DeleteBucketModal';
import UploadModal from '../components/storage/UploadModal';
import UploadProgressPanel from '../components/storage/UploadProgressPanel';
import EmptyState from '../components/storage/EmptyState';
import LoadingSkeleton from '../components/storage/LoadingSkeleton';
import ErrorState from '../components/storage/ErrorState';
import InsufficientBalanceModal from '../components/wallet/InsufficientBalanceModal';
import { KYCRequiredModal } from '../components/kyc';
import { useBuckets } from '../hooks/useBuckets';
import { useBucketObjects } from '../hooks/useBucketObjects';
import { useUploadManager } from '../hooks/useUploadManager';
import { useWallet } from '../hooks/useWallet';
import { useSelectedProject } from '../hooks/useSelectedProject';
import { useKYCStatus } from '../hooks/useKYCStatus';
import { Bucket } from '../types';

const Storage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const projectFromUrl = searchParams.get('project');
  const {
    selectedProjectId: globalProjectId,
    projects,
    setSelectedProject: setGlobalProject,
    autoSelectFirstProject,
  } = useSelectedProject();

  // Use URL param if available, otherwise use global selection
  const selectedProject = projectFromUrl || globalProjectId;

  // Auto-select first project if none selected and no URL param
  useEffect(() => {
    if (!projectFromUrl) {
      autoSelectFirstProject();
    }
  }, [projectFromUrl, autoSelectFirstProject]);
  const [selectedBucket, setSelectedBucket] = useState<Bucket | null>(null);
  const [currentPath, setCurrentPath] = useState<string>('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [bucketToDelete, setBucketToDelete] = useState<Bucket | null>(null);
  const [showKYCModal, setShowKYCModal] = useState(false);

  const { isKYCVerified } = useKYCStatus();

  // Filter and sort states
  const [searchQuery, setSearchQuery] = useState('');
  const [visibilityFilter, setVisibilityFilter] = useState<'all' | 'public' | 'private'>('all');
  const [sortBy, setSortBy] = useState<'created-newest' | 'created-oldest' | 'name-asc' | 'name-desc'>('created-newest');

  // Fetch data
  const { availableBalance, minimumRequired } = useWallet();
  const { buckets, isLoading, isError, refetch, createBucket, isCreating, deleteBucket, isDeleting, deleteError, clearDeleteError, createError, clearCreateError, updateBucket, isUpdating, updateError, clearUpdateError, isInsufficientBalance, balanceErrorMessage, clearInsufficientBalance } = useBuckets(selectedProject);
  const {
    objects,
    isLoading: loadingObjects,
    deleteObject,
    getDownloadUrl
  } = useBucketObjects(
    selectedBucket?.id,
    selectedProject,
    currentPath
  );

  // Global upload manager - persists uploads across page navigation
  const {
    tasks: uploadTasks,
    uploadFiles,
    cancelUpload,
    retryUpload,
    removeTask: dismissUploadTask,
    clearCompleted: dismissAllUploadTasks,
  } = useUploadManager();

  // Filter and sort buckets
  const filteredAndSortedBuckets = useMemo(() => {
    let result = [...buckets];

    // Apply search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter(
        (bucket) =>
          bucket.name.toLowerCase().includes(query) ||
          (bucket.region && bucket.region.toLowerCase().includes(query))
      );
    }

    // Apply visibility filter
    if (visibilityFilter !== 'all') {
      result = result.filter((bucket) =>
        visibilityFilter === 'public' ? bucket.is_public : !bucket.is_public
      );
    }

    // Apply sorting
    result.sort((a, b) => {
      switch (sortBy) {
        case 'created-newest':
          return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
        case 'created-oldest':
          return new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
        case 'name-asc':
          return a.name.localeCompare(b.name);
        case 'name-desc':
          return b.name.localeCompare(a.name);
        default:
          return 0;
      }
    });

    return result;
  }, [buckets, searchQuery, visibilityFilter, sortBy]);

  // Handlers
  const handleProjectChange = (projectId: string) => {
    setGlobalProject(projectId);
    setSelectedBucket(null);
    setCurrentPath('');
  };

  const handleSelectBucket = (bucket: Bucket) => {
    setSelectedBucket(bucket);
    setCurrentPath('');
  };

  const handleCreateBucket = async (data: any) => {
    try {
      await createBucket(data);
      // Only close modal on success
      setShowCreateModal(false);
    } catch (error) {
      // Error is handled in useBuckets hook, modal stays open
    }
  };

  const handleCloseCreateModal = () => {
    clearCreateError();
    setShowCreateModal(false);
  };

  const handleUpload = async (files: FileList | null) => {
    if (!files || !selectedBucket || !selectedProject) return;

    setShowUploadModal(false);

    // Prepare files with paths
    const filesToUpload = Array.from(files).map((file) => ({
      file,
      path: currentPath ? `${currentPath}/${file.name}` : file.name,
    }));

    // Upload files using the global upload manager (persists across navigation)
    await uploadFiles(filesToUpload, selectedBucket.id, selectedProject);
  };

  const handleDownload = async (obj: any) => {
    // Open the tab synchronously (still inside the click's user-activation
    // window) and redirect it once the URL resolves - Safari (and increasingly
    // Chrome/Firefox) block window.open() called after an await.
    const tab = window.open('', '_blank');
    const url = await getDownloadUrl(obj);
    if (url) {
      if (tab) {
        tab.location.href = url;
      } else {
        window.open(url, '_blank');
      }
    } else {
      tab?.close();
    }
  };

  const handleDeleteBucket = (bucket: Bucket) => {
    setBucketToDelete(bucket);
  };

  const handleConfirmDelete = async () => {
    if (!bucketToDelete) return;
    try {
      await deleteBucket(bucketToDelete.id);
      // Close modal and clear selected bucket if it was deleted
      setBucketToDelete(null);
      if (selectedBucket?.id === bucketToDelete.id) {
        setSelectedBucket(null);
      }
    } catch (error) {
      // Error is handled in useBuckets hook, modal stays open
    }
  };

  const handleCloseDeleteModal = () => {
    clearDeleteError();
    setBucketToDelete(null);
  };

  const handleUpdateBucket = async (data: any) => {
    if (!selectedBucket) return;
    try {
      await updateBucket({ id: selectedBucket.id, data });
      // Update the selected bucket locally to reflect changes
      setSelectedBucket((prev) =>
        prev
          ? {
            ...prev,
            ...data,
            is_public: data.is_public !== undefined ? data.is_public : prev.is_public,
          }
          : null
      );
    } catch (error) {
      // Error is handled in useBuckets hook
    }
  };

  const navigateToFolder = (folder: string) => {
    setCurrentPath(folder);
  };

  const navigateUp = () => {
    const parts = currentPath.split('/');
    parts.pop();
    setCurrentPath(parts.join('/'));
  };

  return (
    <div>
      <Header
        title="Object Storage"
        subtitle="S3-compatible storage for your data"
        actions={
          <button
            onClick={() => {
              if (!isKYCVerified) {
                setShowKYCModal(true);
              } else {
                setShowCreateModal(true);
              }
            }}
            className="btn-primary flex items-center gap-2"
            disabled={!selectedProject}
          >
            <PlusIcon className="w-4 h-4" />
            Create Bucket
          </button>
        }
      />

      <div className="p-4 sm:p-6">
        {!projectFromUrl && (
          <ProjectSelector
            projects={projects}
            selectedProject={selectedProject}
            onProjectChange={handleProjectChange}
          />
        )}

        {!selectedProject ? (
          <EmptyState type="no-project" />
        ) : !selectedBucket ? (
          <div className="space-y-3 sm:space-y-4">
            <BucketFilters
              searchQuery={searchQuery}
              onSearchChange={setSearchQuery}
              visibilityFilter={visibilityFilter}
              onVisibilityChange={setVisibilityFilter}
              sortBy={sortBy}
              onSortChange={setSortBy}
            />

            {isError && <ErrorState onRetry={refetch} />}

            {isLoading && <LoadingSkeleton />}

            {!isLoading && !isError && filteredAndSortedBuckets.length === 0 && (
              <EmptyState
                type={searchQuery || visibilityFilter !== 'all' ? 'no-results' : 'no-buckets'}
                onCreateBucket={() => {
                  if (!isKYCVerified) {
                    setShowKYCModal(true);
                  } else {
                    setShowCreateModal(true);
                  }
                }}
              />
            )}

            {!isLoading && !isError && filteredAndSortedBuckets.length > 0 && (
              <BucketListTable
                buckets={filteredAndSortedBuckets}
                onSelectBucket={handleSelectBucket}
                onDeleteBucket={handleDeleteBucket}
              />
            )}
          </div>
        ) : (
          <BucketDetails
            bucket={selectedBucket}
            projectId={selectedProject}
            objects={objects}
            currentPath={currentPath}
            isLoading={loadingObjects}
            onBack={() => {
              setSelectedBucket(null);
              clearUpdateError();
            }}
            onUpload={() => setShowUploadModal(true)}
            onDeleteBucket={() => handleDeleteBucket(selectedBucket)}
            onNavigateFolder={navigateToFolder}
            onNavigateUp={navigateUp}
            onDownload={handleDownload}
            onDeleteObject={deleteObject}
            onUpdateBucket={handleUpdateBucket}
            isUpdating={isUpdating}
            updateError={updateError}
          />
        )}
      </div>

      <CreateBucketModal
        isOpen={showCreateModal}
        onClose={handleCloseCreateModal}
        onCreate={handleCreateBucket}
        isCreating={isCreating}
        apiError={createError}
        projectId={selectedProject}
      />

      <UploadModal
        isOpen={showUploadModal}
        onClose={() => setShowUploadModal(false)}
        onUpload={handleUpload}
      />

      <DeleteBucketModal
        isOpen={!!bucketToDelete}
        bucket={bucketToDelete}
        onClose={handleCloseDeleteModal}
        onDelete={handleConfirmDelete}
        isDeleting={isDeleting}
        deleteError={deleteError}
      />

      <InsufficientBalanceModal
        isOpen={isInsufficientBalance}
        onClose={clearInsufficientBalance}
        message={balanceErrorMessage}
        requiredAmount={minimumRequired}
        currentBalance={availableBalance}
      />

      {/* Upload Progress Panel */}
      <UploadProgressPanel
        uploads={uploadTasks}
        onDismiss={dismissUploadTask}
        onDismissAll={dismissAllUploadTasks}
        onCancel={cancelUpload}
        onRetry={retryUpload}
      />

      {/* KYC Required Modal */}
      <KYCRequiredModal
        isOpen={showKYCModal}
        onClose={() => setShowKYCModal(false)}
      />
    </div>
  );
};

export default Storage;
